import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { db, storage } from '../../config/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const RevisionRequestModal = () => {
  const {
    showRevisionRequestModal,
    setShowRevisionRequestModal,
    revisionRequestProject,
    setProjects,
    showNotification,
    setShowRevisionPaymentModal,
    setRevisionProject,
    setRevisionData,
    revisionData,
    documentTypes,
    getDocumentRevisionFee
  } = useAppContext();

  const [revisionDetails, setRevisionDetails] = useState('');
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [actualFiles, setActualFiles] = useState([]); // Store actual File objects for upload
  const [uploading, setUploading] = useState(false);

  // Reset revision details when modal opens
  useEffect(() => {
    if (showRevisionRequestModal) {
      setRevisionDetails('');
      setSupportingFiles([]);
      setActualFiles([]);
    }
  }, [showRevisionRequestModal]);

  if (!showRevisionRequestModal || !revisionRequestProject) return null;

  // SAFETY CHECK: Stage 1 projects should never have revision requests
  if (revisionRequestProject?.stage === 1) {
    console.error('Stage 1 projects should not have revision requests');
    setShowRevisionRequestModal(false);
    showNotification('Error', 'Revisions are not available for Stage 1 projects', 'error');
    return null;
  }

  // Upload files to Firebase Storage
  const uploadFilesToStorage = async (files, projectId, revisionNumber) => {
    const uploadedFiles = [];
    
    for (const file of files) {
      try {
        const timestamp = Date.now();
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `projects/${projectId}/revisions/revision-${revisionNumber}/${timestamp}_${sanitizedFileName}`;
        
        const storageRef = ref(storage, storagePath);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        uploadedFiles.push({
          name: file.name,
          size: file.size,
          type: file.type,
          url: downloadURL,
          storagePath: storagePath,
          uploadedAt: new Date().toISOString()
        });
        
        console.log('File uploaded:', file.name, downloadURL);
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
        showNotification('Upload Error', `Failed to upload ${file.name}`, 'error');
      }
    }
    
    return uploadedFiles;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    // Store actual File objects for later upload
    setActualFiles(files);
    // Store metadata for display
    const fileList = files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setSupportingFiles(fileList);
  };

  const handleSubmitRevision = async () => {
    if (!revisionDetails.trim()) {
      showNotification("Details Required", "Please describe your revision requirements", "error");
      return;
    }

    // For Stage 3/4 with document type tracking
    if (revisionRequestProject.stage >= 3 && revisionData?.documentType) {
      const documentType = revisionData.documentType;
      const tracking = revisionRequestProject.documentRevisionTracking?.[documentType];
      const nextRevisionNumber = tracking ? tracking.revisionsUsed + 1 : 1;
      const isAdditionalRevision = tracking && nextRevisionNumber > tracking.maxIncluded;
      
      if (isAdditionalRevision) {
        // Check if payment has already been made for this revision
        const paymentKey = `${documentType}_revision${nextRevisionNumber}PaymentStatus`;
        const isPaymentMade = revisionRequestProject[paymentKey] === 'paid';
        
        if (!isPaymentMade) {
          // Store revision details and show payment modal
          const revisionFee = getDocumentRevisionFee(revisionRequestProject.stage, documentType, nextRevisionNumber);
          
          setRevisionData({
            ...revisionData,
            revisionNumber: nextRevisionNumber,
            feedback: revisionDetails,
            supportingFiles: supportingFiles,
            fee: revisionFee
          });
          
          setRevisionProject(revisionRequestProject);
          setShowRevisionRequestModal(false);
          setShowRevisionPaymentModal(true);
          
          showNotification("Payment Required", `Additional revision fee of $${revisionFee} is required for ${revisionData.documentTypeLabel}`, "info");
          return;
        }
      }
      
      // Process the revision request (either included or paid)
      setUploading(true);
      
      setTimeout(() => {
        setProjects(prev => prev.map(p => {
          if (p.id === revisionRequestProject.id) {
            // Update document revision tracking
            const updatedTracking = { ...p.documentRevisionTracking };
            if (!updatedTracking[documentType]) {
              updatedTracking[documentType] = {
                revisionsUsed: 0,
                maxIncluded: 1,
                documents: []
              };
            }
            updatedTracking[documentType].revisionsUsed = nextRevisionNumber;
            
            return {
              ...p,
              status: `revision-${nextRevisionNumber}-requested`,
              documentRevisionTracking: updatedTracking,
              communications: [
                ...p.communications,
                {
                  type: 'client',
                  sender: revisionRequestProject.client,
                  message: `Revision ${nextRevisionNumber} requested for ${revisionData.documentTypeLabel}: ${revisionDetails}${supportingFiles.length > 0 ? ` (${supportingFiles.length} supporting file(s) attached)` : ''}`,
                  timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                },
                {
                  type: "architect",
                  sender: "planAIdesign",
                  message: `We have received your revision request for ${revisionData.documentTypeLabel}${isAdditionalRevision ? ' (additional revision)' : ' (included revision)'}. We will begin working on it and provide an update shortly.`,
                  timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
                }
              ],
              revisionDocuments: [
                ...(p.revisionDocuments || []),
                {
                  documentType: documentType,
                  revisionNumber: nextRevisionNumber,
                  requirements: revisionDetails,
                  files: supportingFiles,
                  uploadDate: new Date().toISOString()
                }
              ]
            };
          }
          return p;
        }));
        
        showNotification(
          "Revision Submitted", 
          `Your revision request for ${revisionData.documentTypeLabel} has been submitted to planAIdesign`, 
          "success"
        );

        setRevisionDetails('');
        setSupportingFiles([]);
        setUploading(false);
        setShowRevisionRequestModal(false);
      }, 1500);
      
    } else {
      // Stage 2 revision logic with proper tracking
      const nextRevisionNumber = (revisionRequestProject.revisions?.currentRound || 0) + 1;
      const isAdditionalRevision = revisionRequestProject.revisions && 
                                  nextRevisionNumber > revisionRequestProject.revisions.maxIncludedRevisions;

      if (isAdditionalRevision) {
        const paymentKey = `revision${nextRevisionNumber}PaymentStatus`;
        const isPaymentMade = revisionRequestProject[paymentKey] === 'paid';
        
        if (!isPaymentMade) {
          // Calculate the correct fee based on project type
          let revisionFee = revisionRequestProject.revisions?.additionalRevisionFee || 150;
          
          // Get more specific fee based on project details
          if (revisionRequestProject.type === 'single' || revisionRequestProject.type === 'duplex') {
            revisionFee = revisionRequestProject.storeys === 1 ? 99 : 150;
          } else if (revisionRequestProject.type === 'townhouse') {
            revisionFee = 150;
          } else if (revisionRequestProject.type === 'secondary' || revisionRequestProject.type === 'minor-renovation' || revisionRequestProject.type === 'major-renovation') {
            revisionFee = 99;
          }
          
          // UPLOAD FILES BEFORE REDIRECTING TO PAYMENT
          // (Files can't survive the Stripe redirect, so upload now)
          setUploading(true);
          
          let uploadedFilesForPayment = [];
          if (actualFiles.length > 0) {
            try {
              showNotification("Uploading", "Uploading supporting files before payment...", "info");
              uploadedFilesForPayment = await uploadFilesToStorage(actualFiles, revisionRequestProject.id, nextRevisionNumber);
              console.log('Files uploaded before payment:', uploadedFilesForPayment.length);
            } catch (uploadError) {
              console.error('Error uploading files:', uploadError);
              showNotification("Upload Error", "Failed to upload files. Please try again.", "error");
              setUploading(false);
              return;
            }
          }
          
          // Store uploaded file info in revisionData (URLs will survive)
          setRevisionData({
            revisionNumber: nextRevisionNumber,
            feedback: revisionDetails,
            supportingFiles: uploadedFilesForPayment, // Now contains URLs from Firebase Storage
            fee: revisionFee
          });
          
          setUploading(false);
          setRevisionProject(revisionRequestProject);
          setShowRevisionRequestModal(false);
          setShowRevisionPaymentModal(true);
          
          showNotification("Payment Required", `Additional revision fee of $${revisionFee} is required before proceeding`, "info");
          return;
        }
      }

      // Process the revision with PROPER TRACKING UPDATE
setUploading(true);

setTimeout(async () => {
  try {
    // Upload files to Firebase Storage first (if any)
    let uploadedFiles = [];
    if (actualFiles.length > 0) {
      showNotification("Uploading", "Uploading supporting files...", "info");
      uploadedFiles = await uploadFilesToStorage(actualFiles, revisionRequestProject.id, nextRevisionNumber);
      console.log('Files uploaded:', uploadedFiles.length);
    }

    // Prepare the update data with uploaded file URLs
    const updateData = {
      status: `revision-${nextRevisionNumber}-requested`,
      hasUsedIncludedRevision: true,
      revisions: {
        currentRound: nextRevisionNumber,
        totalRevisionsUsed: nextRevisionNumber,
        maxIncludedRevisions: revisionRequestProject.revisions?.maxIncludedRevisions || 1,
        additionalRevisionFee: revisionRequestProject.revisions?.additionalRevisionFee || 150
      },
      communications: [
        ...(revisionRequestProject.communications || []),
        {
          type: 'client',
          sender: revisionRequestProject.client,
          message: `Revision ${nextRevisionNumber} requested: ${revisionDetails}${uploadedFiles.length > 0 ? ` (${uploadedFiles.length} supporting file(s) attached)` : ''}`,
          timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
        },
        {
          type: "architect",
          sender: "planAIdesign",
          message: `We have received your revision request${isAdditionalRevision ? ' (payment confirmed)' : ' (included revision)'}. We will begin working on it and provide an update shortly.`,
          timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
        }
      ],
      revisionDocuments: [
        ...(revisionRequestProject.revisionDocuments || []),
        {
          revisionNumber: nextRevisionNumber,
          requirements: revisionDetails,
          files: uploadedFiles, // Now contains URLs from Firebase Storage
          uploadDate: new Date().toISOString()
        }
      ]
    };

    // Update Firebase FIRST
    const projectRef = doc(db, 'projects', revisionRequestProject.id);
    await setDoc(projectRef, updateData, { merge: true });
    console.log('Firebase updated with revision request');

    // Then update local state to match
    setProjects(prev => prev.map(p => {
      if (p.id === revisionRequestProject.id) {
        return {
          ...p,
          ...updateData
        };
      }
      return p;
    }));
    
    showNotification(
      "Revision Submitted", 
      `Your revision request has been submitted to planAIdesign`, 
      "success"
    );

    setRevisionDetails('');
    setSupportingFiles([]);
    setActualFiles([]);
    setUploading(false);
    setShowRevisionRequestModal(false);
    
  } catch (error) {
    console.error('Error submitting revision:', error);
    showNotification('Error', 'Failed to submit revision request. Please try again.', 'error');
    setUploading(false);
  }
}, 500);
}
};

  // Determine revision number and payment status
  const getRevisionInfo = () => {
    if (revisionRequestProject.stage >= 3 && revisionData?.documentType) {
      const tracking = revisionRequestProject.documentRevisionTracking?.[revisionData.documentType];
      const nextRevisionNumber = tracking ? tracking.revisionsUsed + 1 : 1;
      const isAdditionalRevision = tracking && nextRevisionNumber > tracking.maxIncluded;
      const paymentKey = `${revisionData.documentType}_revision${nextRevisionNumber}PaymentStatus`;
      const isPaymentMade = revisionRequestProject[paymentKey] === 'paid';
      
      return {
        nextRevisionNumber,
        isAdditionalRevision,
        isPaymentMade,
        documentTypeLabel: revisionData.documentTypeLabel,
        fee: getDocumentRevisionFee(revisionRequestProject.stage, revisionData.documentType, nextRevisionNumber)
      };
    } else if (revisionRequestProject.stage === 2) {
    // USE currentRound FOR PROPER REVISION TRACKING
    const currentRound = revisionRequestProject.revisions?.currentRound || 0;
    const nextRevisionNumber = currentRound + 1;
    const maxIncluded = revisionRequestProject.revisions?.maxIncludedRevisions || 1;
    const isAdditionalRevision = nextRevisionNumber > maxIncluded;
    const paymentKey = `revision${nextRevisionNumber}PaymentStatus`;
    const isPaymentMade = revisionRequestProject[paymentKey] === 'paid';
    
    // Calculate fee properly for Stage 2
    let fee = 0;
    if (isAdditionalRevision) {
      if (revisionRequestProject.type === 'single' || revisionRequestProject.type === 'duplex') {
        fee = revisionRequestProject.storeys === 1 ? 99 : 150;
      } else if (revisionRequestProject.type === 'townhouse') {
        fee = 150;
      } else if (revisionRequestProject.type === 'secondary' || revisionRequestProject.type === 'minor-renovation' || revisionRequestProject.type === 'major-renovation') {
        fee = 99;
      } else {
        fee = revisionRequestProject.revisions?.additionalRevisionFee || 150;
      }
    }
    
    return {
      nextRevisionNumber,
      isAdditionalRevision,
      isPaymentMade,
      fee: fee
    };
  }
    
    return {
      nextRevisionNumber: 1,
      isAdditionalRevision: false,
      isPaymentMade: false,
      fee: 0
    };
  };

  const revisionInfo = getRevisionInfo();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">
            Request Revision {revisionInfo.nextRevisionNumber}
            {revisionInfo.documentTypeLabel && (
              <span className="text-lg text-gray-600 ml-2">
                - {revisionInfo.documentTypeLabel}
              </span>
            )}
            {revisionInfo.isAdditionalRevision && (
              <span className="text-lg text-orange-600 ml-2">
                (Additional revision - ${revisionInfo.fee} fee {revisionInfo.isPaymentMade ? 'paid ✅' : 'required'})
              </span>
            )}
            {!revisionInfo.isAdditionalRevision && (
              <span className="text-lg text-green-600 ml-2">
                (Included - no fee)
              </span>
            )}
          </h3>
          <button 
            onClick={() => setShowRevisionRequestModal(false)} 
            className="text-2xl text-slate-400 hover:text-slate-600"
          >✕</button>
        </div>

        <div className="mb-6">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h4 className="font-semibold text-lg mb-2">{revisionRequestProject.name}</h4>
            <p className="text-sm text-gray-600">
              Stage {revisionRequestProject.stage}
              {revisionInfo.documentTypeLabel && (
                <span className="block text-xs mt-1">
                  Document Type: {revisionInfo.documentTypeLabel}
                </span>
              )}
              {revisionRequestProject.documentRevisionTracking?.[revisionData?.documentType] && (
                <span className="block text-xs mt-1">
                  Revisions used: {revisionRequestProject.documentRevisionTracking[revisionData.documentType].revisionsUsed} / 
                  {revisionRequestProject.documentRevisionTracking[revisionData.documentType].maxIncluded} included
                </span>
              )}
              {revisionRequestProject.stage === 2 && (
                <span className="block text-xs mt-1">
                  {revisionRequestProject.hasUsedIncludedRevision ? 
                    'Included revision has been used' : 
                    '1 revision included'}
                </span>
              )}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block font-semibold mb-2">
                Revision Requirements <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Please describe in detail what changes you would like made..."
                value={revisionDetails}
                onChange={(e) => setRevisionDetails(e.target.value)}
                rows={6}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">
                Supporting Documents
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="revision-file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <label
                  htmlFor="revision-file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="text-3xl mb-2">📎</div>
                  <p className="text-gray-600 text-center">
                    Click to upload supporting files<br />
                    <span className="text-sm">PDF, JPG, PNG, DOC accepted</span>
                  </p>
                </label>
              </div>
              
              {supportingFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {supportingFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📄</span>
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                      <button
                        onClick={() => {
                          setSupportingFiles(prev => prev.filter((_, i) => i !== index));
                          setActualFiles(prev => prev.filter((_, i) => i !== index));
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {revisionInfo.isAdditionalRevision && !revisionInfo.isPaymentMade && (
            <div className="mt-6 bg-red-50 p-4 rounded-lg border border-red-200">
              <h5 className="font-semibold text-sm mb-2 text-red-800">⚠️ Payment Required First</h5>
              <p className="text-xs text-gray-700">
                This is revision {revisionInfo.nextRevisionNumber} for {revisionInfo.documentTypeLabel || 'this document'}. 
                Your project includes 1 revision.
                An additional fee of <strong>${revisionInfo.fee}</strong> must be paid before this revision can be submitted.
              </p>
              <p className="text-xs text-gray-700 mt-2">
                Click "Continue to Payment" below to process the payment first.
              </p>
            </div>
          )}

          {revisionInfo.isAdditionalRevision && revisionInfo.isPaymentMade && (
            <div className="mt-6 bg-green-50 p-4 rounded-lg border border-green-200">
              <h5 className="font-semibold text-sm mb-2 text-green-800">✅ Payment Confirmed</h5>
              <p className="text-xs text-gray-700">
                Payment of ${revisionInfo.fee} has been received for this additional revision.
                You can now submit your revision request.
              </p>
            </div>
          )}

          {(!revisionInfo.isAdditionalRevision || revisionInfo.isPaymentMade) && (
            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <h5 className="font-semibold text-sm mb-2">What happens next?</h5>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• planAIdesign will review your revision requirements</li>
                <li>• Updated designs will be ready within {
                  revisionRequestProject.stage === 2 ? '1-3 business days' :
                  revisionRequestProject.stage === 3 ? '3-5 business days' :
                  '5-7 business days'
                }</li>
                <li>• You'll receive a notification when the revised documents are ready</li>
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-4 mt-6">
          <button 
            onClick={() => setShowRevisionRequestModal(false)}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmitRevision}
            disabled={uploading || !revisionDetails.trim()}
            className={`flex-1 text-white py-3 rounded-lg font-semibold disabled:bg-gray-400 ${
              revisionInfo.isAdditionalRevision && !revisionInfo.isPaymentMade 
                ? 'bg-purple-600 hover:bg-purple-700' 
                : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            {uploading ? 'Submitting...' : 
             revisionInfo.isAdditionalRevision && !revisionInfo.isPaymentMade ? 'Continue to Payment' : 
             'Submit Revision Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisionRequestModal;